/**
 * Key pool, health tracking and rate budgeting.
 *
 * The single most misunderstood thing about Torn's API: the 100 requests/minute
 * cap is **per account, not per key**. Two keys from your own account buy you
 * failover and nothing else — only keys belonging to *different* players
 * actually raise throughput. The budget below is therefore tracked per
 * `playerId`, and the UI reports accounts rather than keys.
 */

import { ApiError } from "./http.ts";
import { KEY_BACKOFF_CODES, KEY_DEAD_CODES, validateKey } from "./torn.ts";
import { keys$ } from "../state/store.ts";
import type { ApiKeyEntry } from "../state/types.ts";

/** Torn's documented ceiling. */
export const CALLS_PER_MIN_LIMIT = 100;

/** Stay under the ceiling — a burst that trips code 5 costs more than it saves. */
export const SAFETY_MARGIN = 5;

const WINDOW_MS = 60_000;

/** Cooldown applied to an account that reported "too many requests". */
const RATE_LIMIT_COOLDOWN_MS = 30_000;

/** An IP block hits every key at once, so it pauses the whole pool. */
const IP_BLOCK_COOLDOWN_MS = 120_000;

/** Call timestamps per account id; unidentified keys share the `0` bucket. */
const callLog = new Map<number, number[]>();

let globalPauseUntil = 0;

function prune(times: number[], now: number): number[] {
  const cutoff = now - WINDOW_MS;
  let index = 0;
  while (index < times.length && (times[index] as number) < cutoff) index += 1;
  return index === 0 ? times : times.slice(index);
}

/** Calls made in the trailing 60s by one account. */
export function callsInWindow(accountId: number, now = Date.now()): number {
  const times = callLog.get(accountId);
  if (!times) return 0;
  const kept = prune(times, now);
  callLog.set(accountId, kept);
  return kept.length;
}

/** Total calls made in the trailing 60s across every account. */
export function totalCallsInWindow(now = Date.now()): number {
  let total = 0;
  for (const accountId of callLog.keys()) total += callsInWindow(accountId, now);
  return total;
}

function recordCall(accountId: number, now = Date.now()): void {
  const times = prune(callLog.get(accountId) ?? [], now);
  times.push(now);
  callLog.set(accountId, times);
}

export function distinctAccounts(entries: ApiKeyEntry[]): number {
  const ids = new Set<number>();
  for (const entry of entries) {
    if (entry.health === "valid" && entry.playerId !== null) ids.add(entry.playerId);
  }
  return ids.size;
}

/** Ceiling on sustainable calls/min given the keys currently loaded. */
export function budgetPerMin(entries: ApiKeyEntry[]): number {
  return Math.max(0, distinctAccounts(entries) * (CALLS_PER_MIN_LIMIT - SAFETY_MARGIN));
}

export function isGloballyPaused(now = Date.now()): boolean {
  return now < globalPauseUntil;
}

export function globalPauseRemainingMs(now = Date.now()): number {
  return Math.max(0, globalPauseUntil - now);
}

function isUsable(entry: ApiKeyEntry, now: number): boolean {
  if (entry.health === "invalid") return false;
  if (entry.cooldownUntil !== null && now < entry.cooldownUntil) return false;
  return true;
}

/**
 * Picks the key whose account has the most headroom, breaking ties toward the
 * least recently used key so a multi-key account spreads its calls evenly.
 */
export function acquireKey(now = Date.now()): ApiKeyEntry | null {
  if (isGloballyPaused(now)) return null;

  const entries = keys$.get();
  const candidates = entries.filter(
    (entry) => entry.health === "valid" && entry.playerId !== null && isUsable(entry, now),
  );
  if (candidates.length === 0) return null;

  let best: ApiKeyEntry | null = null;
  let bestHeadroom = -1;

  for (const entry of candidates) {
    const used = callsInWindow(entry.playerId as number, now);
    const headroom = CALLS_PER_MIN_LIMIT - SAFETY_MARGIN - used;
    if (headroom <= 0) continue;

    if (
      headroom > bestHeadroom ||
      (headroom === bestHeadroom && (entry.lastUsedAt ?? 0) < (best?.lastUsedAt ?? 0))
    ) {
      best = entry;
      bestHeadroom = headroom;
    }
  }

  return best;
}

/** True when at least one key could serve a call right now. */
export function hasBudget(now = Date.now()): boolean {
  return acquireKey(now) !== null;
}

function patchKey(id: string, patch: Partial<ApiKeyEntry>): void {
  keys$.update((entries) =>
    entries.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)),
  );
}

function markUsed(entry: ApiKeyEntry, now: number): void {
  if (entry.playerId !== null) recordCall(entry.playerId, now);
  patchKey(entry.id, { lastUsedAt: now });
}

/**
 * Maps a Torn failure onto key health. Dead codes retire the key; backoff codes
 * park it briefly; anything else is treated as transient and left alone.
 */
export function reportKeyError(entry: ApiKeyEntry, err: unknown): void {
  if (!(err instanceof ApiError) || err.source !== "torn") return;
  const code = err.code;
  if (code === null) return;

  if (code === 8) {
    // IP block — every key on this connection is affected.
    globalPauseUntil = Date.now() + IP_BLOCK_COOLDOWN_MS;
    patchKey(entry.id, { lastError: err.message });
    return;
  }

  if (KEY_DEAD_CODES.has(code)) {
    patchKey(entry.id, { health: "invalid", lastError: err.message, cooldownUntil: null });
    return;
  }

  if (KEY_BACKOFF_CODES.has(code)) {
    patchKey(entry.id, {
      health: "rateLimited",
      lastError: err.message,
      cooldownUntil: Date.now() + RATE_LIMIT_COOLDOWN_MS,
    });
  }
}

/**
 * Runs one Torn call against a pooled key, recording the spend and translating
 * failures into key health. Returns null when no key has budget.
 */
export async function withKey<T>(
  fn: (key: string, entry: ApiKeyEntry) => Promise<T>,
): Promise<T | null> {
  const now = Date.now();
  const entry = acquireKey(now);
  if (!entry) return null;

  markUsed(entry, now);

  try {
    const result = await fn(entry.key, entry);
    // A call that came back clean rehabilitates a key parked by a backoff code.
    if (entry.health === "rateLimited") {
      patchKey(entry.id, { health: "valid", cooldownUntil: null, lastError: null });
    }
    return result;
  } catch (err) {
    reportKeyError(entry, err);
    throw err;
  }
}

export function newKeyId(): string {
  return `k_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

export interface AddKeyResult {
  ok: boolean;
  message: string;
  /** Set when the key belongs to an account already represented in the pool. */
  duplicateAccount?: boolean;
}

/**
 * Validates a key, then adds it. Duplicate *keys* are rejected outright;
 * duplicate *accounts* are allowed but flagged, since they add failover without
 * adding throughput and the user should know which they are getting.
 */
export async function addKey(rawKey: string, label: string): Promise<AddKeyResult> {
  const key = rawKey.trim();
  if (key.length === 0) return { ok: false, message: "Paste a key first." };

  if (keys$.get().some((entry) => entry.key === key)) {
    return { ok: false, message: "That key is already in the pool." };
  }

  let identity: { playerId: number; playerName: string };
  try {
    identity = await validateKey(key);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { ok: false, message: `Rejected: ${message}` };
  }

  const duplicateAccount = keys$
    .get()
    .some((entry) => entry.playerId === identity.playerId);

  keys$.update((entries) => [
    ...entries,
    {
      id: newKeyId(),
      label: label.trim() || identity.playerName,
      key,
      playerId: identity.playerId,
      playerName: identity.playerName,
      health: "valid",
      cooldownUntil: null,
      lastError: null,
      lastUsedAt: null,
    },
  ]);

  return {
    ok: true,
    duplicateAccount,
    message: duplicateAccount
      ? `Added for ${identity.playerName} — same account as an existing key, so this only adds failover, not speed.`
      : `Added ${identity.playerName} [${identity.playerId}].`,
  };
}

export function removeKey(id: string): void {
  keys$.update((entries) => entries.filter((entry) => entry.id !== id));
}

/** Re-checks one key against Torn and refreshes its health. */
export async function revalidateKey(id: string): Promise<void> {
  const entry = keys$.get().find((candidate) => candidate.id === id);
  if (!entry) return;

  try {
    const identity = await validateKey(entry.key);
    patchKey(id, {
      health: "valid",
      playerId: identity.playerId,
      playerName: identity.playerName,
      cooldownUntil: null,
      lastError: null,
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    const code = err instanceof ApiError ? err.code : null;
    patchKey(id, {
      health: code !== null && KEY_BACKOFF_CODES.has(code) ? "rateLimited" : "invalid",
      lastError: message,
    });
  }
}

/** Validates every stored key. Called once on boot. */
export async function revalidateAll(): Promise<void> {
  await Promise.all(keys$.get().map((entry) => revalidateKey(entry.id)));
}

/** Test seam — resets the rolling window and any global pause. */
export function resetBudget(): void {
  callLog.clear();
  globalPauseUntil = 0;
}
